import { useCallback, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useOrgLockState } from '@/hooks/useOrgLockState';
import LockedPaneCard from '@/components/lock/LockedPaneCard';
import GroupRailSidebar from './GroupRailSidebar';
import type { SidebarGroup, RailBadge } from './GroupRailListItem';

interface Props {
  /** Route base, e.g. "/polls" — group routes are `${basePath}/${groupId}`. */
  basePath: string;
  /** localStorage key remembering the last-viewed group. */
  lastViewKey: string;
  /** localStorage key for the sidebar's collapsed-org set. */
  collapseKey: string;
  /** Hint shown when no group is selected. */
  emptyHint: string;
  /** The :groupId route param from the wrapping feature layout. */
  selectedGroupId: string | null;
  /** Optional per-group count bubbles for the rail (e.g. open polls). */
  badges?: Record<string, RailBadge>;
  /** Content pane for the selected, org-aligned group. */
  renderContent: (group: SidebarGroup) => ReactNode;
}

/**
 * Generic cross-org group workspace: sidebar rail + content pane. Extracted
 * from PrayersLayout (minus the combined All/Lifted views) — the org-rotation
 * and heal choreography is preserved verbatim; see the comments below.
 */
export default function GroupRailLayout({
  basePath,
  lastViewKey,
  collapseKey,
  emptyHint,
  selectedGroupId,
  badges,
  renderContent,
}: Props) {
  const navigate = useNavigate();
  const {
    organizations,
    currentOrganization,
    currentGroup,
    switchOrganization,
    isSwitchingOrg,
    setCurrentGroup,
    isLoading,
  } = useAuth();
  const { isOrgLocked } = useOrgLockState();

  // Resolve the selected group across ALL orgs (not just the current one)
  const selectedGroup = useMemo((): SidebarGroup | null => {
    if (!selectedGroupId) return null;
    for (const membership of organizations) {
      const group = membership.groups?.find((g) => g.id === selectedGroupId);
      if (group) {
        return {
          id: group.id,
          name: group.name,
          orgId: membership.organization_id,
          orgName: membership.organization.name,
          imageUrl: group.group_image_url,
        };
      }
    }
    return null;
  }, [selectedGroupId, organizations]);

  const orgsLoaded = !isLoading && organizations.length > 0;

  // Auto-select on the bare base path: last-used group → current group → first group
  useEffect(() => {
    if (selectedGroupId || !orgsLoaded) return;
    let last: string | null = null;
    try {
      last = localStorage.getItem(lastViewKey);
    } catch {
      // Ignore storage errors
    }
    const lastGroupExists =
      last && organizations.some((o) => o.groups?.some((g) => g.id === last));
    if (lastGroupExists) {
      navigate(`${basePath}/${last}`, { replace: true });
      return;
    }
    if (currentGroup) {
      navigate(`${basePath}/${currentGroup.id}`, { replace: true });
      return;
    }
    const firstGroup = organizations.flatMap((o) => o.groups ?? [])[0];
    if (firstGroup) navigate(`${basePath}/${firstGroup.id}`, { replace: true });
    // No groups at all: stay put and show the empty hint.
  }, [selectedGroupId, orgsLoaded, organizations, currentGroup, navigate, basePath, lastViewKey]);

  // Heal unknown group ids (left the group / bad deep link) once orgs load
  useEffect(() => {
    if (selectedGroupId && orgsLoaded && !selectedGroup) {
      navigate(basePath, { replace: true });
    }
  }, [selectedGroupId, orgsLoaded, selectedGroup, navigate, basePath]);

  // Persist the active group for next visit
  useEffect(() => {
    if (!selectedGroup) return;
    try {
      localStorage.setItem(lastViewKey, selectedGroup.id);
    } catch {
      // Ignore storage errors
    }
  }, [selectedGroup, lastViewKey]);

  // Deep links / refreshes can land on a cross-org group without going
  // through handleSelectGroup — rotate the org context so the pane can
  // mount. If the org context later moves away from a URL we were ALREADY
  // aligned with (the user switched orgs via another feature's rail),
  // FOLLOW the user to the new org's group instead of switching back.
  //
  // alignedGroupRef is only written in the aligned state — never inside the
  // heal branch — so StrictMode's double-invoked effects can't flip the
  // heal into a follow. Heal attempts are capped to avoid a retry loop if
  // the org switch keeps failing (e.g. membership revoked server-side).
  //
  // Follow/heal redirects always target the group root or base path, never
  // a detail segment — a poll/sheet id from the old group would 403.
  const alignedGroupRef = useRef<string | null>(null);
  const healAttemptsRef = useRef<Record<string, number>>({});
  useEffect(() => {
    if (!selectedGroup || !currentOrganization || isSwitchingOrg) return;

    if (selectedGroup.orgId === currentOrganization.id) {
      alignedGroupRef.current = selectedGroup.id;
      healAttemptsRef.current = {};
      return;
    }

    if (alignedGroupRef.current === selectedGroup.id) {
      // User moved the org context out from under the URL — follow them.
      navigate(currentGroup ? `${basePath}/${currentGroup.id}` : basePath, { replace: true });
      return;
    }

    // Deep link / refresh onto a cross-org group: heal by rotating the org.
    const attempts = healAttemptsRef.current[selectedGroup.id] ?? 0;
    if (attempts >= 2) {
      navigate(basePath, { replace: true });
      return;
    }
    healAttemptsRef.current[selectedGroup.id] = attempts + 1;
    switchOrganization(selectedGroup.orgId, selectedGroup.id).catch((e) =>
      console.warn(`[GroupRail:${basePath}] Org switch for deep link failed:`, e)
    );
  }, [
    selectedGroup,
    currentOrganization,
    isSwitchingOrg,
    switchOrganization,
    currentGroup,
    navigate,
    basePath,
  ]);

  const handleSelectGroup = useCallback(
    async (group: SidebarGroup) => {
      navigate(`${basePath}/${group.id}`);

      // Cross-org group: rotate org context (claims) so REST writes work.
      if (group.orgId !== currentOrganization?.id) {
        try {
          await switchOrganization(group.orgId, group.id);
        } catch (e) {
          console.warn(`[GroupRail:${basePath}] Org switch on group select failed:`, e);
        }
      } else {
        // Keep the app-level current group in sync with this pane
        const membership = organizations.find((o) => o.organization_id === group.orgId);
        const fullGroup = membership?.groups.find((g) => g.id === group.id);
        if (fullGroup && fullGroup.id !== currentGroup?.id) setCurrentGroup(fullGroup);
      }
    },
    [
      navigate,
      basePath,
      currentOrganization?.id,
      switchOrganization,
      organizations,
      setCurrentGroup,
      currentGroup?.id,
    ]
  );

  return (
    <div className="flex h-full">
      <GroupRailSidebar
        selectedGroupId={selectedGroupId}
        onSelectGroup={handleSelectGroup}
        collapseKey={collapseKey}
        badges={badges}
      />
      <div className="flex min-w-0 flex-1 flex-col">
        {selectedGroup ? (
          isOrgLocked(selectedGroup.orgId) ? (
            // Paused org: show the placeholder instead of firing doomed fetches.
            <LockedPaneCard organizationId={selectedGroup.orgId} />
          ) : currentOrganization?.id === selectedGroup.orgId ? (
            renderContent(selectedGroup)
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-flock-navy-400">
              Switching organization…
            </div>
          )
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-flock-navy-400">
            {orgsLoaded ? emptyHint : 'Loading…'}
          </div>
        )}
      </div>
    </div>
  );
}
